import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getNovels, deleteNovel } from '../../services/api';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Plus, Edit, Trash2, BookOpen } from 'lucide-react';

const AdminNovels = () => {
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNovels();
  }, []);

  const fetchNovels = async () => {
    try {
      const data = await getNovels();
      setNovels(data);
    } catch (error) {
      console.error('Error fetching novels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (novelId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}" and all its chapters?`)) {
      return;
    }

    try {
      await deleteNovel(novelId);
      toast({
        title: "Novel deleted",
        description: "The novel and all chapters have been removed."
      });
      fetchNovels();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete novel.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={16} />Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-serif text-gray-900">Manage Novels</h1>
            <Link to="/admin/novel/new">
              <Button className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800">
                <Plus size={16} />Add New Novel
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {novels.length === 0 ? (
          <div className="bg-white border border-gray-200 p-12 text-center">
            <BookOpen size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">No novels yet. Create your first novel!</p>
            <Link to="/admin/novel/new">
              <Button className="bg-gray-900 text-white hover:bg-gray-800">
                <Plus size={16} className="mr-2" />Create Novel
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {novels.map((novel) => (
              <div key={novel.id} className="bg-white border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-serif text-gray-900">{novel.title}</h3>
                      <span className={`text-xs uppercase tracking-wider px-2 py-1 ${novel.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {novel.status}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{novel.description}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{novel.totalChapters} chapters</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-6">
                    <Link to={`/admin/novel/chapters/${novel.id}`}>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <BookOpen size={14} />Chapters
                      </Button>
                    </Link>
                    <Link to={`/admin/novel/edit/${novel.id}`}>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <Edit size={14} />Edit
                      </Button>
                    </Link>
                    <Button onClick={() => handleDelete(novel.id, novel.title)} variant="outline" size="sm" className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-600">
                      <Trash2 size={14} />Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNovels;